import "./my-kit-boot";
import { updateMyKitTracker } from "./update-my-kit-tracker";
import { updateLocalStorageAlert } from "./update-local-storage-alert";
import { createCookie } from "../helpers/read-write-cookies";
import { loadMyKitCookie } from "./load-my-kit-cookie";
import { parseKey, uniqueBaseIds } from "./my-kit-utils";

const _qtyTimers = new Map(); // compositeKey -> timeout id
function debounceQty(key, fn, delay = 150) {
    if (_qtyTimers.has(key)) clearTimeout(_qtyTimers.get(key));
    const t = setTimeout(fn, delay);
    _qtyTimers.set(key, t);
}

// Override the form panel heading on the My Kit page based on whether the
// kit has items. With items: "Request a Quote" — the kit JSON is attached to
// the submission. Without items: restore the CMS-set heading.
function updateMyKitFormHeading(hasItems) {
    const formPanel = document.querySelector(".form-panel");
    if (!formPanel) return;
    const heading = formPanel.querySelector(".form-heading");
    if (!heading) return;

    if (hasItems) {
        if (heading.dataset.myKitOriginal === undefined) {
            heading.dataset.myKitOriginal = heading.textContent;
        }
        heading.textContent = "Request a Quote";
    } else if (heading.dataset.myKitOriginal !== undefined) {
        heading.textContent = heading.dataset.myKitOriginal;
    }
}

function renderMyKit() {
    const gridMyKitPanel = document.querySelector(".grid-my-kit-panel");
    if (!gridMyKitPanel) return;

    const myKitAdditionalElement = gridMyKitPanel.querySelector("[data-my-kit-additional]");
    let myKitCookie = loadMyKitCookie();
    if (!myKitCookie) return;

    const allKeys = Object.keys(myKitCookie);
    // Avoid a flash of the CMS default heading before the fetch resolves.
    updateMyKitFormHeading(allKeys.length > 0);

    if (allKeys.length === 0) {
        if (myKitAdditionalElement) myKitAdditionalElement.dataset.active = "false";
        const grids = gridMyKitPanel.querySelectorAll("[data-my-kit-grid]");
        grids.forEach(g => {
            g.innerHTML = "";
            g.dataset.active = "false";
        });
        const headings = gridMyKitPanel.querySelectorAll("[data-my-kit-grid-heading]");
        headings.forEach(h => h.dataset.active = "false");
        return;
    }

    const template = gridMyKitPanel.querySelector("template[data-my-kit-template]");
    const imgPlaceholderTemplate = document.querySelector("template[data-my-kit-template-image-placeholder]");
    if (!template) {
        console.error("Missing template element.");
        return;
    }

    const entryIds = uniqueBaseIds(allKeys);

    fetch("/actions/frontline/kit/entries", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            [window.csrfTokenName]: window.csrfTokenValue
        },
        body: JSON.stringify({ entryIds })
    })
        .then(response => response.json())
        .then(data => {
            const categories = ["equipment", "vehicles"];
            let validCards = false;
            let categoryCount = 0;

            const headings = gridMyKitPanel.querySelectorAll("[data-my-kit-grid-heading]");
            for (const heading of headings) heading.dataset.active = "false";

            const keysById = {};
            for (const key of allKeys) {
                const { id } = parseKey(key);
                if (!keysById[id]) keysById[id] = [];
                keysById[id].push(key);
            }

            categories.forEach(category => {
                const entries = data[category] || [];
                const grid = gridMyKitPanel.querySelector(`[data-my-kit-grid="${category}"]`);
                if (grid) {
                    grid.innerHTML = "";
                    grid.dataset.active = "false";
                }

                if (entries.length > 0 && grid) {
                    let renderedAny = false;

                    entries.forEach(entry => {
                        const id = String(entry.id);
                        const relatedKeys = keysById[id] || [];
                        if (relatedKeys.length === 0) return;

                        for (const key of relatedKeys) {
                            const { variant } = parseKey(key);

                            const clone = template.content.cloneNode(true);
                            const titleEl = clone.querySelector("[data-my-kit-title]");
                            const subtitleEl = clone.querySelector("[data-my-kit-subtitle]");
                            const descriptionEl = clone.querySelector("[data-my-kit-description]");
                            const imgEl = clone.querySelector("[data-my-kit-image]");
                            const variantEl = clone.querySelector("[data-my-kit-variant]");
                            const buttonEl = clone.querySelector("[data-remove-from-kit]");
                            const quantityInput = clone.querySelector("[data-my-kit-quantity]");

                            if (titleEl) titleEl.textContent = entry.title ?? "";
                            if (subtitleEl) subtitleEl.textContent = entry.subtitle ?? "";
                            if (descriptionEl) descriptionEl.textContent = entry.description ?? "";

                            if (variantEl) {
                                if (variant) variantEl.textContent = variant;
                                else variantEl.remove();
                            }

                            if (buttonEl) {
                                buttonEl.dataset.removeFromKit = key;
                            }

                            if (imgEl) {
                                if (entry.image) {
                                    imgEl.src = entry.image;
                                    imgEl.alt = entry.title ?? "";
                                } else if (imgPlaceholderTemplate) {
                                    const placeholderClone = imgPlaceholderTemplate.content.cloneNode(true);
                                    imgEl.replaceWith(placeholderClone);
                                }
                            }

                            if (quantityInput) {
                                const qty = Number(myKitCookie[key]?.quantity ?? 1);
                                quantityInput.value = Number.isFinite(qty) && qty > 0 ? String(qty) : "1";

                                quantityInput.addEventListener("input", function () {
                                    const raw = Number(this.value);
                                    const newQty = Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : 1;
                                    this.value = String(newQty);

                                    debounceQty(key, () => {
                                        let liveCookie = loadMyKitCookie();
                                        liveCookie[key] = {
                                            ...(liveCookie[key] || {}),
                                            quantity: newQty,
                                            timestamp: liveCookie[key]?.timestamp || Date.now()
                                        };
                                        createCookie("myKit", JSON.stringify(liveCookie), 365);

                                        // Local update then broadcast to others
                                        updateMyKitTracker();
                                        updateLocalStorageAlert();
                                    });
                                });

                                quantityInput.addEventListener("blur", function () {
                                    const raw = Number(this.value);
                                    const newQty = Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : 1;
                                    this.value = String(newQty);
                                });
                            }

                            grid.appendChild(clone);
                            renderedAny = true;
                        }
                    });

                    if (renderedAny) {
                        validCards = true;
                        categoryCount++;
                        grid.dataset.active = "true";
                    }
                }
            });

            if (categoryCount >= 2) {
                for (const heading of headings) heading.dataset.active = "true";
            }

            if (myKitAdditionalElement) {
                myKitAdditionalElement.dataset.active = validCards ? "true" : "false";
            }

            updateMyKitFormHeading(validCards);
        })
        .catch(err => {
            console.error("Failed to fetch kit entries:", err);
        });
}

// Initial load
window.addEventListener("load", function () {
    const gridMyKitPanel = document.querySelector(".grid-my-kit-panel");
    if (!gridMyKitPanel) return;

    renderMyKit();

    window.addEventListener("click", function (e) {
        const target = e.target.closest("button[data-remove-from-kit]");
        if (!target) return;

        const compositeKey = target.dataset.removeFromKit;
        if (!compositeKey) return;

        let myKitCookie = loadMyKitCookie();
        if (!myKitCookie) return;

        try {
            delete myKitCookie[compositeKey];
            createCookie("myKit", JSON.stringify(myKitCookie), 365);
            updateMyKitTracker();
            updateLocalStorageAlert(); // broadcast mutation
            renderMyKit();
        } catch (err) {
            console.error("Error removing item from kit:", err);
        }
    });

    // Ignore our own storage events
    window.addEventListener("storage", (event) => {
        if (event.key !== "myKitUpdated") return;
        let payload = null;
        try { payload = JSON.parse(event.newValue); } catch {}
        if (payload && payload.source && payload.source === window.__MY_KIT_TAB_ID__) return;

        renderMyKit();
        updateMyKitTracker();
    });
});
